import {
  advancedFormControl,
  advancedFormArray,
  FormControlTypeEnum,
} from '@magieno/common';
import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import {
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  validateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';
import { CompanyTypeEnum } from '../../../../../enums/company-type.enum';
import { array, type } from '@pristine-ts/data-mapping-common';
import { ValidationGroupEnum } from '../../../../../enums/validation-group.enum';
import { CalculationDate } from '../../../../../models/calculation.date';
import { CorporateIncomeTaxConnectedCorporationOptions } from './corporate-income-tax-connected-corporation.options';

@translationBaseKey('income-tax-corporation-common-options')
export class CorporateIncomeTaxCommonOptions {
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année fiscale',
        en: 'Fiscal year',
      },
      tooltip: {
        fr: 'L\'année fiscale de la société pour laquelle le calcul de l\'impôt est effectué.',
        en: 'The fiscal year of the company for which the tax calculation is performed.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional({ groups: [ValidationGroupEnum.SalaryDividend] })
  fiscalYear: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de début de l\'exercice',
        en: 'Start date of the fiscal year',
      },
      tooltip: {
        fr: 'La date de début de l\'exercice financier de la société.',
        en: 'The start date of the company\'s fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @type(() => new CalculationDate())
  fiscalYearStart: CalculationDate;
  //date_debut_exercice

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de fin de l\'exercice',
        en: 'Fiscal year end date',
      },
      tooltip: {
        fr: 'La date de fin de l\'exercice financier de la société.',
        en: 'The end date of the company\'s fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @type(() => new CalculationDate())
  fiscalYearEnd: CalculationDate;
  //date_fin_exercice

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Type de société',
        en: 'Type of company',
      },
      tooltip: {
        fr: 'Le type de société (par exemple: société privée sous contrôle canadien toute l\'année, société publique, société privée).',
        en: 'The type of company (e.g., Canadian-controlled private corporation all year long, public company, private company).',
      },
    },
    items: [
      {
        label: {
          fr: 'Société privée sous contrôle canadien toute l\'année',
          en: 'Canadian-controlled private corporation all year long',
        },
        id: CompanyTypeEnum.ccpcAllYear,
        //sPCC_annee_complete
      },
      {
        label: {
          fr: 'Société publique',
          en: 'Public Company',
        },
        id: CompanyTypeEnum.publicCompany,
        //societe_publique
      },
      {
        label: {
          fr: 'Société privée',
          en: 'Private Company',
        },
        id: CompanyTypeEnum.privateCompany,
        //societe_privee
      },
    ],
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(CompanyTypeEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  companyType: CompanyTypeEnum;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Capital imposable des sociétés associées de l\'année actuelle ou l\'année précédente, selon le cas (voir 125(5.1))',
        en: 'Taxable capital of associated corporations in the current year or the previous year, as the case may be (see 125(5.1))',
      },
      tooltip: {
        fr: 'Le montant du capital imposable détenu par les sociétés associées de la société pour l\'année en cours ou l\'année précédente, selon le cas. Ce montant est utilisé dans le calcul de certains crédits d\'impôt.',
        en: 'The amount of taxable capital held by the associated corporations of the company for the current year or previous year, as the case may be. This amount is used in the calculation of certain tax credits.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  // @validateIf((object: CorporateIncomeTaxCommonOptions, root, currentPath) => object.companyType !== CompanyTypeEnum.ccpcAllYear)
  totalTaxableCapital: number = 0;
  //capital_imposable_total

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Revenus d\'entreprises',
        en: 'Business revenues',
      },
      tooltip: {
        fr: 'Les revenus totaux générés par l\'entreprise pour l\'année fiscale.',
        en: 'The total revenues generated by the business for the fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  businessIncome: number;
  //rEEA

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Revenu de placement total ajusté de la société pour l\'année précédente',
        en: 'Corporation\'s total adjusted investment income for the previous year',
      },
      tooltip: {
        fr: 'Le montant du revenu de placement total ajusté de la société pour l\'année fiscale précédente.',
        en: 'The amount of the corporation\'s total adjusted investment income for the previous fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  // @validateIf((object: CorporateIncomeTaxCommonOptions, root, currentPath) => object.companyType !== CompanyTypeEnum.ccpcAllYear)
  adjustedInvestmentIncome: number = 0;
  //revenu_placement_total_ajuste_precedent

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Dons',
        en: 'Donations',
      },
      tooltip: {
        fr: 'Le montant total des dons effectués par la société pour l\'année fiscale.',
        en: 'The total amount of donations made by the corporation for the fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  donations: number = 0;
  //dons

  // @todo: Eventually, this should be specific in federal and provincial. See discussion on January 9th, 2024
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Solde du report des pertes en capital à la fin de l’année d’imposition précédente',
        en: 'Capital loss carryforward balance',
      },
      tooltip: {
        fr: 'Le montant total du solde du report des pertes en capital pour l\'année fiscale. Le solde appliqué sera utilisé autant au fédéral qu\'au provincial.',
        en: 'The total amount of the capital loss carryforward balance for the fiscal year. The balance will be applied both for federal and provincial.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  capitalLossBalance: number;
  //report_Pc

  // @todo: Eventually, this should be specific in federal and provincial. See discussion on January 9th, 2024
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Solde du report des pertes autres qu’en capital à la fin de l’année d’imposition précédente',
        en: 'Non-capital loss carryforward balance',
      },
      tooltip: {
        fr: 'Le montant total du solde du report des pertes autres qu\'en capital pour l\'année fiscale. Le solde appliqué sera utilisé autant au fédéral qu\'au provincial.',
        en: 'The total amount of the non-capital loss carryforward balance for the fiscal year. The balance will be applied both for federal and provincial.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  nonCapitalLossBalance: number;
  //report_PAQC

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Plafond des affaires de la société',
        en: 'Company\'s business limit',
      },
      tooltip: {
        fr: 'Le plafond des affaires de la société pour l\'année fiscale.',
        en: 'The company\'s business limit for the fiscal year.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: CorporateIncomeTaxCommonOptions, root, currentPath) => object.companyType === CompanyTypeEnum.ccpcAllYear)
  assignedBusinessLimit?: number;
  //plafondAttribue125_3_2

  // todo this seems to be used only for federal for now.
  @array(CorporateIncomeTaxConnectedCorporationOptions)
  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 1,
    elementClassType: CorporateIncomeTaxConnectedCorporationOptions,
  })
  @ValidateNested()
  connectedCorporations: CorporateIncomeTaxConnectedCorporationOptions[] = [];
}

